import React, { useEffect, useState, useCallback } from 'react';
import { getDepts, createDept, updateDept, refreshDeptHierarchy } from '../../api/deptApi';
import Papa from 'papaparse';
import DeptView from './DeptView';
import DeptEdit from './DeptEdit';
import DeptForm from './DeptForm';   // ✅ 등록 컴포넌트 이름 변경
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { Box, Typography, Button, Stack, Paper } from '@mui/material';
import {
  Groups,
  Add as AddIcon,
  FileUpload as UploadIcon,
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  AccountTree as AccountTreeIcon
} from '@mui/icons-material';
import '../../css/dept.css';

function DeptList() {
  const [deptTree, setDeptTree] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [mode, setMode] = useState('view'); // view | edit | new
  const [csvFile, setCsvFile] = useState(null);
  const [expandedItems, setExpandedItems] = useState([]);

  // 모든 부서 코드 추출 (자동 펼침용)
  const getAllDeptCodes = useCallback((nodes) => {
    let codes = [];
    nodes.forEach(node => {
      codes.push(node.dept_code);
      if (node.children?.length > 0) {
        codes = [...codes, ...getAllDeptCodes(node.children)];
      }
    });
    return codes;
  }, []);

  const loadTree = useCallback(() => {
    getDepts().then(res => {
      const tree = buildDeptTree(res.data);
      setDeptTree(tree);
      // 부서 목록 로드 시 자동으로 모두 펼침
      setExpandedItems(getAllDeptCodes(tree));
    });
  }, [getAllDeptCodes]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const buildDeptTree = (depts) => {
    const map = {};
    const tree = [];
    depts.forEach(dept => {
      map[dept.dept_code] = { ...dept, children: [] };
    });
    depts.forEach(dept => {
      if (dept.parent_dept_code && map[dept.parent_dept_code]) {
        map[dept.parent_dept_code].children.push(map[dept.dept_code]);
      } else {
        tree.push(map[dept.dept_code]);
      }
    });
    return tree;
  };

  const handleCsvProcess = () => {
    if (!csvFile) {
      alert('CSV 파일을 먼저 선택하세요.');
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      encoding: "UTF-8",
      complete: async (result) => {
        const allDepts = await getDepts();
        for (const row of result.data) {
          try {
            const exists = allDepts.data.find(d => d.dept_code === row.dept_code);
            if (exists) {
              await updateDept(row.dept_code, row);
              console.log(`✅ Updated: ${row.dept_code}`);
            } else {
              await createDept(row);
              console.log(`➕ Created: ${row.dept_code}`);
            }
          } catch (err) {
            console.error('CSV 처리 오류:', err);
          }
        }
        await refreshDeptHierarchy();
        loadTree();
        alert('CSV 업로드 완료!');
      },
    });
  };

  const handleRefreshHierarchy = async () => {
    try {
      await refreshDeptHierarchy();
      loadTree();
      alert('부서 상·하위 정보 재정리 완료!');
    } catch (err) {
      console.error('부서 재정리 오류:', err);
      alert('부서 재정리 실패');
    }
  };

  const renderMuiTree = (nodes) => (
    nodes.map(node => (
      <TreeItem
        key={node.dept_code}
        itemId={node.dept_code}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
            <Groups sx={{ mr: 1, fontSize: '1.2rem', color: '#ffa000' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {node.dept_name} <small className="text-muted">({node.dept_code})</small>
            </Typography>
          </Box>
        }
      >
        {node.children.length > 0 && renderMuiTree(node.children)}
      </TreeItem>
    ))
  );

  return (
    <div className="row mt-4">
      {/* 좌측: 부서 트리 */}
      <div className="col-6 border-end">
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          mb: 3,
          pl: 0.5
        }}>
          <Box sx={{ width: 5, height: 26, bgcolor: '#1976d2', borderRadius: 2 }} />
          <AccountTreeIcon sx={{ color: '#1976d2', fontSize: '1.7rem' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', fontSize: '1.3rem' }}>
            부서 트리 관리
          </Typography>
          <Box sx={{ 
            px: 1, 
            py: 0.1, 
            bgcolor: '#f1f3f5', 
            borderRadius: '4px', 
            fontSize: '0.65rem', 
            color: '#6c757d', 
            fontWeight: 'bold',
            border: '1px solid #dee2e6',
            ml: 0.5
          }}>
            ORG STRUCTURE
          </Box>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {/* ✅ 부서 등록 버튼 */}
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedDept(null);
              setMode('new');
            }}
            sx={{ height: '32px', fontWeight: 'bold', fontSize: '0.8rem' }}
          >
            부서 등록
          </Button>

          {/* ✅ CSV 업로드 영역 */}
          <Box sx={{ display: 'flex', alignItems: 'center', height: '32px', border: '1px solid #ced4da', borderRadius: '4px', overflow: 'hidden' }}>
            <Button
              variant="text"
              component="label"
              size="small"
              sx={{
                height: '100%',
                bgcolor: '#f8f9fa',
                color: '#495057',
                borderRadius: 0,
                borderRight: '1px solid #ced4da',
                px: 2,
                fontSize: '0.8rem',
                '&:hover': { bgcolor: '#e9ecef' }
              }}
            >
              파일 선택
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={(e) => setCsvFile(e.target.files[0])}
              />
            </Button>
            <Typography
              variant="caption"
              sx={{
                px: 1.5,
                color: csvFile ? '#212529' : '#6c757d',
                minWidth: '120px',
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {csvFile ? csvFile.name : '선택된 파일 없음'}
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<UploadIcon />}
            onClick={handleCsvProcess}
            sx={{ height: '32px', fontSize: '0.8rem' }}
          >
            업로드
          </Button>

          <Button
            variant="outlined"
            color="info"
            size="small"
            startIcon={<DownloadIcon />}
            href="/sampleFile/deptSample.csv"
            download="deptSample.csv"
            sx={{ height: '32px', fontSize: '0.8rem' }}
          >
            샘플 다운로드
          </Button>

          <Button
            variant="contained"
            color="warning"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefreshHierarchy}
            sx={{ height: '32px', color: '#fff', fontWeight: 'bold', fontSize: '0.8rem' }}
          >
            부서 재정리
          </Button>
        </Stack>
        <div className="dept-tree-container border p-3 bg-white rounded" style={{ height: '600px', overflowY: 'auto' }}>
          <SimpleTreeView
            expandedItems={expandedItems}
            onSelectedItemsChange={(event, itemId) => {
              if (itemId) {
                setSelectedDept(itemId);
                setMode('view');
              }
            }}
          >
            {renderMuiTree(deptTree)}
          </SimpleTreeView>
        </div>
      </div>

      {/* 우측: 상세 / 편집 / 등록 */}
      <div className="col-6">
        {mode === 'view' && selectedDept && (
          <DeptView
            code={selectedDept}
            onEdit={() => setMode('edit')}
            onDeleted={() => {
              loadTree();
              setSelectedDept(null);
            }}
          />
        )}
        {mode === 'edit' && selectedDept && (
          <DeptEdit
            code={selectedDept}
            onCancel={() => setMode('view')}
            onUpdated={() => {
              loadTree();
              setMode('view');
            }}
          />
        )}
        {mode === 'new' && (
          <DeptForm
            onCancel={() => setMode('view')}
            onCreated={() => {
              loadTree();
              setMode('view');
            }}
          />
        )}
        {!selectedDept && mode === 'view' && (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: '#fcfcfc',
              borderStyle: 'dashed',
              borderRadius: 2,
              mt: 8
            }}
          >
            <Typography color="text.secondary">
              👈 왼쪽 트리에서 부서를 선택하여 상세 정보를 확인하세요.
            </Typography>
          </Paper>
        )}
      </div>
    </div>
  );
}

export default DeptList;